import { API_ENDPOINTS } from "../config/constants";
import { cacheProducts, getCachedProducts } from "./productListCache";
import { isFullOfflineModeEnabled, subscribeToFullOfflineMode } from "./fullOfflineMode";
import { cachePosMasterData, getCachedPosMasterData } from "./posMasterDataCache";
import { isElectronOfflineRuntime } from "../offline/runtime";
import { enqueueMutation, listMutations } from "../offline/mutationQueue";
import { runMutationSync } from "../offline/syncEngine";

let syncTimer = null;
const SYNC_INTERVAL_MS = 30_000;

const isDesktop = isElectronOfflineRuntime;

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `product-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const publishChange = (detail = {}) => {
  window.dispatchEvent(new CustomEvent("desktop-product-sync-change", { detail }));
};

export const persistProductCaches = async (restaurantId, products) => {
  if (!Array.isArray(products)) return;
  await Promise.all([
    cacheProducts(restaurantId, products, "master"),
    cacheProducts(restaurantId, products, "pos"),
  ]);
  try {
    localStorage.setItem("productlistx", JSON.stringify(products));
  } catch {}
  publishChange({ type: "local-product-list-updated", restaurantId });
};

export const getCachedCategories = async (restaurantId) => {
  const master = await getCachedPosMasterData(restaurantId, "categories_master");
  if (Array.isArray(master?.records) && master.records.length) return master.records;
  const pos = await getCachedPosMasterData(restaurantId, "categories");
  if (Array.isArray(pos?.records) && pos.records.length) return pos.records;
  try {
    const stored = JSON.parse(localStorage.getItem("categorylistx") || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

export const persistCategoryCaches = async (restaurantId, categories) => {
  if (!Array.isArray(categories)) return;
  await Promise.all([
    cachePosMasterData(restaurantId, "categories_master", categories),
    cachePosMasterData(restaurantId, "categories", categories),
  ]);
  try {
    localStorage.setItem("categorylistx", JSON.stringify(categories));
  } catch {}
  publishChange({ type: "local-category-list-updated", restaurantId });
};

export const enqueueProductMutation = async ({ restaurantId, operation, fields, localProduct }) => {
  if (!isDesktop()) return null;
  const id = newId();
  const entityKey = operation === "create"
    ? `offline-${id}`
    : String(fields?.productid || fields?.id || "") || null;
  const categoryDependency = String(fields?.productcategoryid || "").startsWith("offline-category-")
    ? String(fields.productcategoryid).replace("offline-category-", "")
    : null;
  await enqueueMutation({
    id,
    restaurantId,
    entityType: "product",
    action: operation,
    endpoint: endpointFor(operation),
    payload: fields,
    metadata: {
      transport: operation === "delete" ? "json" : "multipart",
      localProduct,
      localOnly: isFullOfflineModeEnabled(),
    },
    entityKey,
    dependsOn: categoryDependency,
  });
  publishChange({ type: "queued", id, restaurantId, operation, fields, mutationId: id });
  return id;
};

export const enqueueProductRequest = async ({ restaurantId, operation, endpoint, fields, id: requestedId, metadata = {} }) => {
  if (!isDesktop()) return null;
  const id = requestedId || newId();
  const isCategory = operation.startsWith("category-");
  const productId = String(fields?.productid || "");
  const entityKey = isCategory
    ? operation === "category-create"
      ? `offline-category-${id}`
      : String(fields?.productcategoryid || "") || null
    : productId || null;
  const dependsOn = productId.startsWith("offline-")
    ? productId.replace("offline-", "")
    : null;
  await enqueueMutation({
    id,
    restaurantId,
    entityType: isCategory ? "category" : "product",
    action: isCategory ? operation.replace("category-", "") : operation,
    endpoint,
    payload: fields,
    metadata: { transport: "json", ...metadata, localOnly: isFullOfflineModeEnabled() },
    entityKey,
    dependsOn,
  });
  publishChange({ type: "queued", id, restaurantId, operation, fields, mutationId: id });
  return id;
};

export const enqueueCategoryMutation = async ({ restaurantId, operation, endpoint, fields }) => {
  const categoryId = String(fields?.productcategoryid || "").trim();
  const requestedId = operation === "category-create"
    ? undefined
    : `${operation}-${restaurantId}-${categoryId}`;
  const id = await enqueueProductRequest({
    restaurantId,
    operation,
    endpoint,
    fields,
    id: requestedId,
    metadata: { entity: "category" },
  });
  return {
    id,
    localCategoryId: operation === "category-create" ? `offline-category-${id}` : categoryId,
  };
};

const dataUrlToBlob = (dataUrl, type = "application/octet-stream") => {
  const encoded = String(dataUrl || "").split(",")[1] || "";
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));
  return new Blob([bytes], { type });
};

const toFormData = (fields = {}) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value?.__offlineFile && value.dataUrl) {
      form.append(key, dataUrlToBlob(value.dataUrl, value.type), value.name || "upload");
    } else if (value !== undefined && value !== null) {
      form.append(key, String(value));
    }
  });
  return form;
};

const endpointFor = (operation) => {
  if (operation === "create") return API_ENDPOINTS.ADD_PRODUCT;
  if (operation === "update") return API_ENDPOINTS.EDIT_PRODUCT;
  return API_ENDPOINTS.DELETE_PRODUCT;
};

const replaceCreatedProduct = async (restaurantId, entry, result, activeEntries = []) => {
  const createdId = result?.id ?? result?.product_id ?? result?.data?.id ?? result?.data?.productid;
  if (!createdId) return;
  const cached = await getCachedProducts(restaurantId, "master");
  if (Array.isArray(cached)) {
    const next = cached.map((product) =>
      product?.__offlineMutationId === entry.id
        ? { ...product, ...(result?.data || {}), id: createdId, productid: createdId, __offlinePending: false, __offlineMutationId: undefined }
        : product
    );
    await persistProductCaches(restaurantId, next);
  }
  await remapPendingReferences(entry.id, "productid", `offline-${entry.id}`, createdId, activeEntries);
};

const remapPendingReferences = async (sourceEntryId, fieldName, localId, serverId, activeEntries = []) => {
  const pending = await listMutations({
    entityTypes: ["product", "category"],
    includeDeferred: true,
    includeBlocked: true,
  });
  for (const queued of pending || []) {
    if (queued.id === sourceEntryId) continue;
    const fields = queued.payload || {};
    if (String(fields[fieldName] || "") !== String(localId)) continue;
    await enqueueMutation({
      ...queued,
      payload: { ...fields, [fieldName]: serverId },
    });
  }
  for (const queued of activeEntries) {
    const fields = queued.payload || {};
    if (queued.id !== sourceEntryId && String(fields[fieldName] || "") === String(localId)) {
      queued.payload = { ...fields, [fieldName]: serverId };
    }
  }
};

const replaceCreatedCategory = async (restaurantId, entry, result, activeEntries = []) => {
  const createdId = result?.id ?? result?.productcategoryid ?? result?.data?.id ?? result?.data?.productcategoryid;
  if (!createdId) return;
  const localId = `offline-category-${entry.id}`;
  const cachedCategories = await getCachedCategories(restaurantId);
  const nextCategories = cachedCategories.map((category) =>
    String(category?.id) === localId
      ? { ...category, ...(result?.data || {}), id: createdId, __offlinePending: false, __offlineMutationId: undefined }
      : category
  );
  await persistCategoryCaches(restaurantId, nextCategories);

  const cachedProducts = await getCachedProducts(restaurantId, "master");
  if (Array.isArray(cachedProducts)) {
    await persistProductCaches(restaurantId, cachedProducts.map((product) =>
      String(product?.productcategoryid) === localId
        ? { ...product, productcategoryid: createdId }
        : product
    ));
  }
  await remapPendingReferences(entry.id, "productcategoryid", localId, createdId, activeEntries);
};

export const syncPendingProductMutations = async () => {
  return runMutationSync({
    key: "catalog",
    entityTypes: ["product", "category"],
    execute: async (entry) => {
      const useJson = entry.action === "delete" || entry.metadata?.transport === "json";
      const response = await fetch(entry.endpoint || endpointFor(entry.action), {
        method: "POST",
        body: useJson ? JSON.stringify(entry.payload) : toFormData(entry.payload),
        headers: useJson ? { "Content-Type": "application/json" } : undefined,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.success === false || result?.status === false) {
        const error = new Error(result?.message || `Server returned ${response.status}`);
        error.permanent = response.status >= 400 && response.status < 500;
        throw error;
      }
      return result;
    },
    onSuccess: async (entry, result, entries) => {
      if (entry.entityType === "product" && entry.action === "create") {
        await replaceCreatedProduct(entry.restaurantId, entry, result, entries);
      }
      if (entry.entityType === "category" && entry.action === "create") {
        await replaceCreatedCategory(entry.restaurantId, entry, result, entries);
      }
      publishChange({
        type: "synced", id: entry.id, restaurantId: entry.restaurantId,
        operation: entry.entityType === "category" ? `category-${entry.action}` : entry.action,
      });
    },
  });
};

export const startProductSyncService = () => {
  if (!isDesktop() || syncTimer) return;
  const run = () => syncPendingProductMutations().catch(() => {});
  window.addEventListener("online", run);
  subscribeToFullOfflineMode((offlineEnabled) => {
    if (!offlineEnabled) run();
  });
  syncTimer = window.setInterval(run, SYNC_INTERVAL_MS);
  run();
};
